import { forwardRef, type ComponentProps, type ReactNode } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Typography } from '@ui/components/base/typography';
import { Tooltip } from '@ui/components/tooltips/Tooltip';
import { cn } from '@ui/lib/utils';

const ITEM_CLASSNAME = [
  'relative h-10 w-full justify-start gap-0 rounded-lg pr-2.5 pl-3',
  'transition-colors duration-150 text-fg',
  'hover:bg-fg-strong/4 hover:text-fg',
].join(' ');

const ITEM_SELECTED_CLASSNAME = 'bg-accent-soft text-primary hover:bg-accent-soft hover:text-primary';

const ACCENT_CLASSNAME = 'absolute top-2 bottom-2 -left-1 w-0.75 rounded-xs bg-primary';

const BADGE_CLASSNAME = 'absolute top-0.5 right-0.5 size-2 rounded-full bg-primary ring-2 ring-surface-2';

const LABEL_CLASSNAME = [
  'ml-3 min-w-0 flex-1 truncate text-left text-sm leading-5 font-medium',
  'transition-opacity duration-150',
  'group-data-[collapsed=true]/nav-item:ml-0 group-data-[collapsed=true]/nav-item:w-0',
  'group-data-[collapsed=true]/nav-item:flex-none group-data-[collapsed=true]/nav-item:overflow-hidden',
  'group-data-[collapsed=true]/nav-item:opacity-0 group-data-[collapsed=true]/nav-item:pointer-events-none',
].join(' ');

interface SidebarNavItemProps extends Omit<ComponentProps<typeof Button>, 'children'> {
  icon: ReactNode;
  label: string;
  selected?: boolean;
  collapsed?: boolean;
  badge?: boolean;
}

const SidebarNavItem = forwardRef<HTMLButtonElement, SidebarNavItemProps>(
  ({ icon, label, selected = false, collapsed = false, badge = false, className, ...props }, ref) => {
    const item = (
      <Button
        ref={ref}
        variant={ButtonVariant.GHOST}
        data-collapsed={collapsed}
        aria-current={selected ? 'page' : undefined}
        className={cn('group/nav-item', ITEM_CLASSNAME, selected && ITEM_SELECTED_CLASSNAME, className)}
        {...props}
      >
        {selected ? <span aria-hidden className={ACCENT_CLASSNAME} /> : null}
        <span className="relative flex size-7 shrink-0 items-center justify-center">
          {icon}
          {badge ? <span aria-hidden className={BADGE_CLASSNAME} /> : null}
        </span>
        <Typography bold={selected} className={LABEL_CLASSNAME}>
          {label}
        </Typography>
      </Button>
    );

    if (collapsed) {
      return (
        <Tooltip content={label} side="right" triggerClassName="block w-full">
          {item}
        </Tooltip>
      );
    }
    return item;
  }
);

SidebarNavItem.displayName = 'SidebarNavItem';

export { SidebarNavItem };
export type { SidebarNavItemProps };
